import { useState, useCallback, useEffect } from 'react';
import useDebounce from 'hooks/use-debounce';
import { fetchAllInteractionPurposes } from 'utils/interaction-purposes-utils';
import EverTrue from 'app';

/**
 * Custom hook for debounced interaction purposes search
 * Similar to global search but specifically for purposes
 *
 * @param {number} debounceMs - Debounce delay in milliseconds (default: 300)
 * @returns {object} Search state and handlers
 */
export const useInteractionPurposesSearch = (debounceMs = 300) => {
  const [purposes, setPurposes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [query, setQuery] = useState('');

  // Use the custom useDebounce hook for debounced query
  const debouncedQuery = useDebounce(query, debounceMs);

  // Perform search when debounced query changes
  useEffect(() => {
    const performSearch = async (searchQuery) => {
      setLoading(true);
      try {
        const results = await fetchAllInteractionPurposes(searchQuery, 100);
        setPurposes(results);
      } catch (error) {
        EverTrue.Alert.error('Error searching purposes:', error);
        setPurposes([]);
      } finally {
        setLoading(false);
      }
    };

    performSearch(debouncedQuery);
  }, [debouncedQuery]);

  const handleSearch = useCallback((newQuery) => {
    setQuery(newQuery);
  }, []);

  const clearSearch = useCallback(() => {
    setQuery('');
  }, []);

  return {
    purposes,
    loading,
    query,
    handleSearch,
    clearSearch,
  };
};

export default useInteractionPurposesSearch;
